import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Checkbox, composeProductTitle } from '@glplus/glpweb-dsl';
import { vendorApiClient, type AnswerResult, type ProductQuestion, type SentProduct, type SuggestedProduct } from '../../lib/vendorApiClient';
import { notifyProductsChanged } from '../../lib/productEvents';
import { MUTED, describeProductError } from './parts';

/** "Boar's Head Ovengold Roasted Turkey Breast" — the brand once, however the name is stored. */
export function suggestedTitle(s: SuggestedProduct): string {
  return composeProductTitle(s.brand, s.name);
}

/** Where the vendor's version came from: typed in, or a row of a file. */
export function sentSource(t: TFunction, sent: SentProduct): string {
  return sent.rowNumber == null ? t('products.questions.source.typed') : t('products.questions.source.row', { row: sent.rowNumber });
}

/** Where in their file the row is: 'Tab "Central" · row 31', or the file's name when it has no tabs. */
export function sentPlace(t: TFunction, sent: SentProduct): string | null {
  if (sent.rowNumber == null) return null;
  const row = t('products.questions.place.row', { row: sent.rowNumber });
  if (sent.sheet) return `${t('products.questions.place.tab', { tab: sent.sheet })} · ${row}`;
  return sent.file ? `${sent.file} · ${row}` : row;
}

export const WORDED_REASONS = ['barcode_name_differs', 'barcode_details_differ', 'name_similar', 'staff_proposal'] as const;
export type WordedReason = (typeof WORDED_REASONS)[number];

/** Which reason's wording a question takes; anything unknown reads as a barcode conflict. */
export function reasonKey(question: Pick<ProductQuestion, 'reason'>): WordedReason {
  return WORDED_REASONS.find((r) => r === question.reason) ?? 'barcode_name_differs';
}

/** How our product's barcode stands beside the vendor's, as a key under products.questions.ourBarcode. */
export function ourBarcodeKey(question: Pick<ProductQuestion, 'barcodeMatches' | 'suggestedBarcode' | 'reason'>): 'same' | 'different' | 'none' | 'similarName' {
  if (question.barcodeMatches || question.suggestedBarcode === 'same') return 'same';
  if (question.reason === 'name_similar') return 'similarName';
  return question.suggestedBarcode === 'different' ? 'different' : 'none';
}

/** The vendor's barcode as they typed it, or that they said it has none. */
export function sentBarcode(t: TFunction, sent: SentProduct): string {
  return sent.barcode ? t('products.questions.barcode', { barcode: sent.barcode }) : t('products.barcode.none');
}

export type QuestionOutcome =
  | { kind: 'answered'; result: AnswerResult }
  | { kind: 'handed' };

/** What to tell the vendor once a question is dealt with. */
export function outcomeNotice(t: TFunction, name: string, outcome: QuestionOutcome): string {
  if (outcome.kind === 'handed') return t('products.questions.done.handed', { name });
  const { result } = outcome;
  if (result.outcome === 'confirmed') return t('products.questions.done.confirmed', { name });
  if (result.outcome === 'created_new') return t('products.questions.done.createdNew', { name, barcode: result.assignedBarcode ?? '' });
  return t('products.questions.done.handed', { name });
}

/**
 * Yes / No / "let us sort this out" for one question. The checkbox does not
 * act on its own — ticking it swaps the two answers for one "Send it to us"
 * button, so a stray click on a checkbox cannot hide a product from the vendor.
 */
export function QuestionActions({
  vendorId,
  question,
  canResolve,
  detailsTo,
  onDone,
}: {
  vendorId: string;
  question: ProductQuestion;
  canResolve: boolean;
  detailsTo?: React.ReactNode;
  onDone: (outcome: QuestionOutcome) => void;
}) {
  const { t } = useTranslation();
  const [handOff, setHandOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (work: () => Promise<QuestionOutcome>) => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await work();
      notifyProductsChanged();
      onDone(outcome);
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setBusy(false);
    }
  };
  const answer = (value: 'yes' | 'no') =>
    void run(async () => ({ kind: 'answered', result: await vendorApiClient.answerQuestion(vendorId, question.questionId, value) }));
  const send = () =>
    void run(async () => {
      await vendorApiClient.handOffProduct(vendorId, question.vendorProductId);
      return { kind: 'handed' };
    });

  if (!canResolve) {
    return (
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ flex: 1, fontSize: 14, color: MUTED }}>{t('products.questions.ownerAnswers')}</Typography>
        {detailsTo}
      </Box>
    );
  }

  const id = question.questionId;
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }} data-testid={`question-handoff-${id}`}>
          <Checkbox
            ariaLabel={t('products.questions.letUsSort')}
            label={t('products.questions.letUsSort')}
            checked={handOff}
            disabled={busy}
            onChange={(e) => setHandOff(e.target.checked)}
            size="small"
          />
        </Box>
        {detailsTo}
        {handOff ? (
          <Button
            ariaLabel={t('products.questions.sendToUs')}
            buttonText={t('products.questions.sendToUs')}
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={send}
            dataTestId={`question-send-${id}`}
          />
        ) : (
          <>
            <Button
              ariaLabel={t('products.questions.no')}
              buttonText={t('products.questions.no')}
              variant="outlinedGreen"
              disabled={busy}
              onClick={() => answer('no')}
              dataTestId={`question-no-${id}`}
            />
            <Button
              ariaLabel={t('products.questions.yes')}
              buttonText={t('products.questions.yes')}
              variant="contained"
              color="primary"
              disabled={busy}
              onClick={() => answer('yes')}
              dataTestId={`question-yes-${id}`}
            />
          </>
        )}
      </Box>
      {error && (
        <Typography role="alert" sx={{ mt: 1, fontSize: 14, fontWeight: 700, color: '#C62828' }} data-testid={`question-error-${id}`}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
